import dayjs, { type Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import i18n from 'i18n';

import { type preferredTimeDisplayModeTypes } from 'types/customer.types';
import { DATE_FORMAT } from 'utils/constants/dateTime';
import {
  getLocaleDateFormat,
  getLocaleDateTimeFormat,
  getLocaleDateTimeFormatWithSeconds,
} from './locale-date-utils';

dayjs.extend(utc);
dayjs.extend(timezone);

interface UtcFormattedRange {
  startUtcISO: string | null;
  endUtcISO: string | null;
  startUtcDate: Date | null;
  endUtcDate: Date | null;
}

export const parseSafeDate = (val: string | Date | number): Date => {
  if (val instanceof Date) return val;
  if (typeof val === 'number') return new Date(val);
  if (typeof val === 'string') {
    const trimmed = val.trim();
    const normalized =
      trimmed.includes(' ') && !trimmed.includes('T')
        ? trimmed.replace(' ', 'T')
        : trimmed;
    const d = new Date(normalized);
    if (!isNaN(d.getTime())) return d;
    const parsedDayjs = dayjs(val);
    if (parsedDayjs.isValid()) return parsedDayjs.toDate();
  }
  return new Date(val);
};

export const formatDate = (rawDate: string): string => {
  const date = parseSafeDate(rawDate);

  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

export const formatDateToString = (
  date: Date | string,
  format: string = 'MMM DD, YYYY'
): string => {
  return dayjs(date).format(format);
};

export const calculateTimeStampFromCurrentTime = (
  timestamp: number
): number => {
  const currentTimestamp = Date.now();
  return currentTimestamp - timestamp;
};

export const isWithinLast24Hours = (timestamp: number): boolean => {
  const difference = calculateTimeStampFromCurrentTime(timestamp);
  const millisecondsIn24Hours = 24 * 60 * 60 * 1000;

  return difference <= millisecondsIn24Hours;
};

export const todaysDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth() + 1;
  const day = today.getDate();

  return `${year}-${month < 10 ? `0${month}` : month}-${
    day < 10 ? `0${day}` : day
  }`;
};

//  format date to YYYY-MM-DD
export const formatDateShortener = (val: Date | string | number): string => {
  const date = parseSafeDate(val);

  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Adding 1 because months are zero-based
  const day = date.getDate().toString().padStart(2, '0');

  const formattedDate = `${year}-${month}-${day}`;
  return formattedDate;
};

export const convertSeconds = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  seconds %= 3600;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = (seconds % 60).toFixed(0);

  return { hours, minutes, seconds: remainingSeconds };
};

export const convertSecondsWithUnitValues = (
  seconds: number | string
): string => {
  if (seconds === '0.0' || seconds === 0) {
    return '0 sec';
  }

  if (!seconds) {
    return '--';
  }

  let remainingSeconds = Math.round(Number(seconds));
  const hours = Math.floor(remainingSeconds / 3600);
  remainingSeconds %= 3600;
  const minutes = Math.floor(remainingSeconds / 60);
  remainingSeconds %= 60;

  const timeParts = [];

  if (hours > 0) {
    timeParts.push(`${hours} hr`);
  }
  if (minutes > 0 || hours > 0) {
    timeParts.push(`${minutes} min`);
  }
  timeParts.push(`${remainingSeconds} sec`);

  return timeParts.join(' ');
};

export const convertSecondsApprox = (seconds: number | string): string => {
  if (seconds === '0.0' || seconds === 0) return '0 min';
  if (seconds === null || seconds === undefined || seconds === '') return '--';

  let remaining = Math.round(Number(seconds));
  let hours = Math.floor(remaining / 3600);
  remaining %= 3600;
  let minutes = Math.floor(remaining / 60);
  const secs = remaining % 60;

  // Round seconds to nearest minute
  if (secs >= 30) minutes += 1;

  // Carry overflow minutes into hours
  if (minutes >= 60) {
    hours += Math.floor(minutes / 60);
    minutes = minutes % 60;
  }

  if (hours === 0 && minutes === 0) return '<1 min';

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours} hr`);
  if (minutes > 0 || hours > 0) parts.push(`${minutes} min`);

  return parts.join(' ');
};

/**
 * Checks if a given date string is within a specified number of days from the current date or a given date.
 * @param dateString The date string to check.
 * @param noOfDays The number of days to consider for the comparison (default is 7 days).
 * @param startDate An optional parameter to specify a custom date for comparison. It defaults to today if no value supplied
 * @param endDate An optional parameter to specify a custom enddate for comparison. It defaults to today if no value supplied
 * @returns A boolean indicating whether the input date is within the specified range.
 */
export const isWithinGivenDays = (
  dateString: string,
  noOfDays = 7,
  startDate?: string,
  endDate?: string
) => {
  const inputDate = parseSafeDate(dateString);
  inputDate.setHours(0, 0, 0, 0);
  if (startDate && endDate) {
    const startDateFormat = parseSafeDate(startDate);
    const endDateFormat = parseSafeDate(endDate);

    startDateFormat.setHours(0, 0, 0, 0);
    endDateFormat.setHours(0, 0, 0, 0);
    return inputDate >= startDateFormat && inputDate <= endDateFormat;
  } else {
    const today = startDate ? parseSafeDate(startDate) : new Date();

    today.setHours(0, 0, 0, 0);

    const xDaysAgo = new Date();
    xDaysAgo.setDate(today.getDate() - noOfDays);

    return inputDate >= xDaysAgo && inputDate <= today;
  }
};

/**
 * Formats a given date string into a human-readable format displaying the month and year.
 * e.g "2024-09-01 00:00:00.000" to "September 2024"
 * @param dateString - The date string to be formatted.
 * @returns A string representing the formatted date in "Month Year" format.
 */
export const formatDateToMonthYear = (dateString: string): string => {
  const date = parseSafeDate(dateString);
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
  };

  return new Intl.DateTimeFormat('en-US', options).format(date);
};

export const formatTimestamp = (
  timestamp: number | string,
  customFormat?: string
): string => {
  const date = dayjs(timestamp);
  const formattedDate = date.format(customFormat ?? 'YYYY-MMM-DD HH:mm (Z)');
  return formattedDate;
};

// Function to format the 'age' value into a more readable format
// Input format example: '1 04:38:40.000' (1 day, 4 hours, 38 mins, 40 secs)
// Desired output: '1 day, 4 hours, 38 mins and 40 secs'
export const formatAge = (age: string): string => {
  // Regular expression to capture days, hours, minutes, and seconds from the age string
  const ageRegex = /(\d+) (\d{2}):(\d{2}):(\d{2})\.\d+/;

  // Use the regex to match the 'age' string
  const match = age.match(ageRegex);

  // If the format matches, proceed with the transformation
  if (match) {
    // Extract the matched groups and convert them to numbers for proper formatting
    const [, days, hours, minutes, seconds] = match.map(Number);
    const and = i18n.t('duration_and');
    const dayWord = i18n.t(
      days > 1 ? 'duration_day_plural' : 'duration_day_singular'
    );
    const hourWord = i18n.t(
      hours > 1 ? 'duration_hour_plural' : 'duration_hour_singular'
    );
    const minWord = i18n.t(
      minutes > 1 ? 'duration_min_abbr_plural' : 'duration_min_abbr_singular'
    );
    const secWord = i18n.t(
      seconds > 1 ? 'duration_sec_abbr_plural' : 'duration_sec_abbr_singular'
    );

    // Initialize an empty result string to build the formatted age
    let result = '';

    // Add days if greater than 0
    if (days > 0) result += `${days} ${dayWord}, `;

    // Add hours if greater than 0
    if (hours > 0) result += `${hours} ${hourWord}, `;

    // Only add minutes if they are greater than 0
    if (minutes > 0) result += `${minutes} ${minWord} ${and} `;

    // Add seconds
    result += `${seconds} ${secWord}`;

    // Return the fully formatted string, removing any trailing commas
    return result;
  }

  // If no match is found (unlikely based on input), return the original age string
  return age;
};

export const formatQuickRange = (range: string): string => {
  const mapping = {
    '1D': '1 Day',
    '7D': '7 Days',
    '14D': '14 Days',
    '1M': '1 Month',
    '1Y': '1 Year',
    YTD: 'Year to Date',
  } as const;

  return range in mapping ? mapping[range as keyof typeof mapping] : range;
};

/**
 * Get the user's locale from browser or fallback to 'en-US'
 */
export function getUserLocale(): string {
  if (typeof window !== 'undefined') {
    return navigator.language || navigator.languages?.[0] || 'en-US';
  }
  return 'en-US';
}

// Dynamic formats based on user locale
export const getDynamicFormats = (locale?: string) => {
  const userLocale = locale ?? getUserLocale();

  return {
    DATE_FORMAT: getLocaleDateFormat(userLocale),
    DATE_TIME_FORMAT: getLocaleDateTimeFormat(userLocale),
    DATE_TIME_FORMAT_SECONDS: getLocaleDateTimeFormatWithSeconds(userLocale),
  };
};

/**
 * Core function to convert UTC time to formatted time based on display mode and timezone
 * @param utcTime - UTC timestamp (can be Date, string, or number)
 * @param timeDisplayMode - How to display the time (local, configured, utc)
 * @param customerTimezone - Customer's configured timezone (used when mode is 'configured')
 * @param format - Optional custom format string (defaults to "MMM D, YYYY h:mm:ss A")
 * @returns Formatted time string
 */
export function convertUtcToDisplayTime(
  utcTime: Date | string | number,
  timeDisplayMode: preferredTimeDisplayModeTypes,
  customerTimezone: string,
  customFormat?: string
): string {
  // Automatically detect user's locale
  const userLocale = getUserLocale();

  // Get locale-appropriate format if no custom format provided
  let format: string;
  if (customFormat) {
    format = customFormat;
  } else {
    // Automatically use locale-specific format
    const formats = getDynamicFormats(userLocale);
    format = formats.DATE_TIME_FORMAT_SECONDS;
  }

  // Set dayjs locale automatically
  dayjs.locale(userLocale.split('-')[0]);

  // Parse the UTC time
  const utcMoment = dayjs.utc(utcTime);

  switch (timeDisplayMode) {
    case 'local':
      return utcMoment.local().format(format);

    case 'configured':
      if (!customerTimezone) {
        return utcMoment.local().format(format);
      }
      return utcMoment.tz(customerTimezone).format(format);

    case 'utc':
      return utcMoment.format(format) + ' UTC';

    default:
      console.warn(
        `Unknown timeDisplayMode: ${timeDisplayMode}. Falling back to UTC.`
      );
      return utcMoment.format(format) + ' UTC';
  }
}

/**
 * Converts display time back to UTC based on preferred timezone setting
 */
export const convertDisplayTimeToUtc = (
  displayDate: Date | string,
  preferredTimeDisplayMode: preferredTimeDisplayModeTypes,
  configuredTimezone?: string
): Date => {
  console.log({ displayDate, preferredTimeDisplayMode, configuredTimezone }); // TODO: remove logs before pushing to prod
  switch (preferredTimeDisplayMode) {
    case 'utc':
      return dayjs.utc(displayDate).toDate();

    case 'local':
      return dayjs(displayDate).utc().toDate();

    case 'configured':
      if (configuredTimezone) {
        return dayjs.tz(displayDate, configuredTimezone).utc().toDate();
      }
      return dayjs(displayDate).utc().toDate();

    default:
      return dayjs(displayDate).utc().toDate();
  }
};

/**
 * Convert standard date string to locale date string based on user's default format
 * This function is useful when the value is only a date string and not a timestamp
 * @param dateString - The date string to be converted
 * @param overrideFormat - The format to be used for conversion
 * @returns The locale date string
 */
export const convertStdDateString = (
  dateString: string | number | Date,
  overrideFormat?: string
): string => {
  return dayjs(dateString).format(overrideFormat ?? DATE_FORMAT);
};

export const formatStartEndDateUTC = (
  rangeSelected: string,
  startDate?: Date | Dayjs | null,
  endDate?: Date | Dayjs | null,
  timezoneOverride?: string
): UtcFormattedRange => {
  const tz = timezoneOverride ?? dayjs.tz.guess();

  let start: Dayjs | null = null;
  let end: Dayjs | null = null;

  const yesterDay = dayjs().tz(tz).subtract(1, 'day');

  switch (rangeSelected) {
    case '1D': {
      start = yesterDay.startOf('day');
      end = yesterDay.add(1, 'day').startOf('day');
      break;
    }

    case 'Today': {
      const date = dayjs().tz(tz);
      start = date.startOf('day');
      end = date.add(1, 'day').startOf('day');
      break;
    }

    case '7D': {
      start = yesterDay.subtract(6, 'day').startOf('day');
      end = yesterDay.add(1, 'day').startOf('day');
      break;
    }

    case '14D': {
      start = yesterDay.subtract(13, 'day').startOf('day');
      end = yesterDay.add(1, 'day').startOf('day');
      break;
    }

    case '30D': {
      start = yesterDay.subtract(30, 'day').startOf('day');
      end = yesterDay.add(1, 'day').startOf('day');
      break;
    }

    case '1M': {
      start = yesterDay.subtract(1, 'month').startOf('day');
      end = yesterDay.add(1, 'day').startOf('day');
      break;
    }

    case '1Y': {
      start = yesterDay.subtract(1, 'year').startOf('day');
      end = yesterDay.add(1, 'day').startOf('day');
      break;
    }

    case 'Custom': {
      if (startDate && endDate) {
        start = dayjs(startDate).tz(tz).startOf('day');
        end = dayjs(endDate).tz(tz).add(1, 'day').startOf('day');
      }
      break;
    }

    default: {
      const date = dayjs().tz(tz);
      start = date.startOf('day');
      end = date.add(1, 'day').startOf('day');
      break;
    }
  }

  return {
    startUtcISO: start ? start.utc().toISOString() : null,
    endUtcISO: end ? end.utc().toISOString() : null,
    startUtcDate: start ? start.utc().toDate() : null,
    endUtcDate: end ? end.utc().toDate() : null,
  };
};

export function getPastDaysRangeInTz(timezone: string, daysAgo: number) {
  const today = dayjs().tz(timezone);

  return {
    startDateTimeInTz: today.subtract(daysAgo, 'day').startOf('day'),
    endDateTimeInTz: today.add(1, 'day').startOf('day'),
  };
}

export const guessUserTimezone = (): string => {
  return dayjs.tz.guess(); // e.g., 'Asia/Kolkata', 'America/New_York'
};

// to get date
export const getUtcISOStringOfSelectedTz = (options?: {
  date?: string | Date | dayjs.Dayjs | null;
  tz?: string;
  startOfDay?: boolean;
}): string => {
  const timezone = options?.tz ?? guessUserTimezone();
  const inputDate = options?.date ? dayjs(options.date) : dayjs();
  const localized = inputDate.tz(timezone);
  const target = options?.startOfDay ? localized.startOf('day') : localized;
  return target.utc().toISOString();
};

export const getDateTimeStringWithTz = (date: Dayjs, tz: string) => {
  return date.tz(tz).format('YYYY-MM-DDTHH:mm:ssZ');
};

/**
 * Returns the current UTC offset in minutes for a given IANA timezone.
 *
 * @param timezoneName - e.g. "America/New_York", "Asia/Kolkata"
 * @returns number - e.g. -300 for UTC-5
 */
export const getCurrentUtcOffset = (timezoneName: string): number => {
  return dayjs().tz(timezoneName).utcOffset();
};

/**
 * Formats a date range for use in file names
 * @param startDate - The start date should already be displayed in user's preferred timezone
 * @param endDate - The end date should already be displayed in user's preferred timezone
 * @returns A formatted string in the format "YYYY-MM-DD HH_mm_ss - YYYY-MM-DD HH_mm_ss"
 */
export const formatDateRangeForFileName = (
  startDate: Date | string | number,
  endDate: Date | string | number
): string => {
  const startFormatted = dayjs(startDate).format(DATE_FORMAT);
  const endFormatted = dayjs(endDate).format(DATE_FORMAT);

  return `${startFormatted} - ${endFormatted}`;
};

/**
 * Builds the UTC wall-clock string the GPS endpoints expect ('YYYY-MM-DD HH:mm:ss').
 *
 * Timeline points carry `start_ts`/`end_ts` only when they came straight from the
 * driver-event API; merged and frontend-derived (unknown-gap) points only have the
 * epoch pair in `y`. Passing that epoch through as a raw string made the backend
 * parse it as an invalid Date, which reached Athena as `from_unixtime(NaN / 1000)`
 * and failed with "Column 'nan' cannot be resolved".
 *
 * @returns the formatted timestamp, or '' when no usable value exists — callers
 * must skip the request rather than send an empty range.
 */
export const toGpsApiTimestamp = (
  primary?: string | number | null,
  fallback?: string | number | null
): string => {
  const value = primary ?? fallback;
  if (value === null || value === undefined || value === '') return '';
  const parsed =
    typeof value === 'number' ? dayjs.utc(value) : dayjs.utc(String(value));
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : '';
};
